'use client';
import React, { Suspense, useMemo } from 'react';
import { DateTime } from 'luxon';
import { isoToMillis } from '../../../../../src/components/Quote/formatters';
import { SectionTitle } from '@benzinga/core-ui';
import type { AnalystRatingsSummary, Ratings } from '@benzinga/calendar-manager';
import type { News } from '@benzinga/basic-news-manager';

import type { BaseQuotePageProps } from '../../../../../src/utils/quote/types';
import { useTranslation } from 'react-i18next';
import QuotePageTitleLink from '../_components/QuotePageTitleLink';
import SeeMoreLink from '../_components/SeeMoreLink';
import { seeMoreDestinations } from '../_utils/seeMoreDestinations';
import { QuoteNewsFeed } from '../../../../../src/components/Quote/QuoteNewsFeed';
import { QuoteFooter } from '../../../../../src/components/Quote/QuoteFooter';
import AnalystActionsTable from '../../../../../src/components/Quote/AnalystActionsTable';
import ZacksHeadScript from '../../../../../src/components/Quote/ZacksHeadScript';
import PeopleAlsoWatch from '../_components/PeopleAlsoWatch';
import { ChartPlotSkeleton } from '../../../../../src/components/Quote/QuoteSkeletons';
import { buildRatingTrendSnapshots, classifyRating } from '../../../../../src/utils/quote/ratingTrend';
import type { EChartsOption } from 'echarts';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FreestarAd })),
);

const COLORS = {
  strongBuy: '#1B7F4F',
  buy: '#30BF60',
  hold: '#9CA3AF',
  sell: '#F97316',
  strongSell: '#EF4444',
  ptArea: 'rgba(99,102,241,0.15)',
  ptLine: '#3730A3',
};

type QuarterlyPT = { label: string; min: number; max: number; median: number; sortKey: number };

const buildQuarterlyPTData = (ratings: Ratings[], summary: AnalystRatingsSummary): QuarterlyPT[] => {
  const byQuarter: Record<string, { pts: number[]; sortKey: number }> = {};

  for (const r of ratings) {
    if (!r.date || !r.pt_current) continue;
    const dt = DateTime.fromISO(r.date);
    if (!dt.isValid) continue;
    const q = Math.ceil(dt.month / 3);
    const label = `Q${q}'${dt.toFormat('yy')}`;
    const sortKey = dt.year * 4 + q;
    if (!byQuarter[label]) byQuarter[label] = { pts: [], sortKey };
    const pt = Number(r.pt_current);
    if (!isNaN(pt) && pt > 0) byQuarter[label].pts.push(pt);
  }

  const quarters: QuarterlyPT[] = Object.entries(byQuarter)
    .filter(([, { pts }]) => pts.length > 0)
    .map(([label, { pts, sortKey }]) => {
      const sorted = [...pts].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
      return { label, min: sorted[0], max: sorted[sorted.length - 1], median, sortKey };
    })
    .sort((a, b) => a.sortKey - b.sortKey);

  const nowMin = summary.lowestPriceTarget ? Number(summary.lowestPriceTarget) : null;
  const nowMax = summary.highestPriceTarget ? Number(summary.highestPriceTarget) : null;
  const nowMedian = summary.consensusPriceTarget ? Number(summary.consensusPriceTarget) : null;
  if (nowMin && nowMax && nowMedian) {
    quarters.push({ label: 'Now', min: nowMin, max: nowMax, median: nowMedian, sortKey: 99999 });
  }

  return quarters;
};

const PriceTargetRangeChart: React.FC<{ ratings: Ratings[]; summary: AnalystRatingsSummary }> = ({
  ratings,
  summary,
}) => {
  const data = useMemo(() => buildQuarterlyPTData(ratings, summary), [ratings, summary]);
  const option = useMemo((): EChartsOption => {
    if (!data.length) return {} as EChartsOption;
    const labels = data.map(quarter => quarter.label);
    const medianData = data.map(quarter => quarter.median);
    const allPts = data.flatMap(quarter => [quarter.min, quarter.max]);
    const yMin = Math.floor((Math.min(...allPts) * 0.85) / 10) * 10;

    return {
      grid: { left: 60, right: 24, top: 20, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        formatter: (params: { axisValue: string; seriesName: string; value: number }[]) => {
          const label = params[0]?.axisValue ?? '';
          const dataIndex = labels.indexOf(label);
          const quarter = data[dataIndex];
          if (!quarter) return '';
          return [
            `<strong>${label}</strong>`,
            `<span style="color:${COLORS.ptLine}">● Consensus: $${quarter.median.toFixed(0)}</span>`,
            `<span style="color:#6366F1">■ Range: $${quarter.min.toFixed(0)} – $${quarter.max.toFixed(0)}</span>`,
          ].join('<br/>');
        },
      },
      legend: {
        data: ['Analyst PT Range', 'Consensus Median'],
        bottom: 0,
        textStyle: { fontSize: 10 },
        icon: 'roundRect',
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { fontSize: 10 },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: yMin,
        axisLabel: { fontSize: 10, formatter: (value: number) => `$${value}` },
        splitLine: { lineStyle: { color: '#f0f0f0' } },
      },
      series: [
        {
          name: '_ptBase',
          type: 'bar',
          stack: 'ptRange',
          data: data.map(quarter => quarter.min),
          itemStyle: { color: 'rgba(0,0,0,0)', borderColor: 'rgba(0,0,0,0)' },
          emphasis: { itemStyle: { color: 'rgba(0,0,0,0)', borderColor: 'rgba(0,0,0,0)' } },
          silent: true,
        },
        {
          name: 'Analyst PT Range',
          type: 'bar',
          stack: 'ptRange',
          data: data.map(quarter => quarter.max - quarter.min),
          itemStyle: { color: COLORS.ptArea, borderColor: 'rgba(99,102,241,0.25)' },
          barMaxWidth: 40,
          emphasis: { itemStyle: { color: COLORS.ptArea } },
        },
        {
          name: 'Consensus Median',
          type: 'line',
          data: medianData,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: COLORS.ptLine, width: 2 },
          itemStyle: { color: COLORS.ptLine },
          z: 10,
        },
      ],
    } as unknown as EChartsOption;
  }, [data]);

  if (!data.length) return null;
  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">
        Price Target Range — {data.length > 1 ? `${data.length - 1} Quarters` : ''}
      </p>
      <Suspense fallback={<ChartPlotSkeleton heightClassName="h-[260px]" label="Loading price target chart" />}>
        <ReactECharts option={option} style={{ height: 260 }} />
      </Suspense>
    </div>
  );
};

const AnalystConsensusPanel: React.FC<{
  ratingsSummary: AnalystRatingsSummary;
  ratings: Ratings[];
  currentPrice?: number | null;
}> = ({ ratingsSummary, ratings, currentPrice }) => {
  const counts = useMemo(() => {
    const latestByFirm: Record<string, string> = {};
    [...ratings]
      .filter(r => r.analyst && r.rating_current)
      .sort((a, b) => isoToMillis(a.date) - isoToMillis(b.date))
      .forEach(r => {
        latestByFirm[r.analyst] = r.rating_current;
      });

    const tally = { strongBuy: 0, buy: 0, hold: 0, sell: 0, strongSell: 0 };
    for (const stance of Object.values(latestByFirm)) {
      const cat = classifyRating(stance);
      if (cat) tally[cat]++;
    }
    return tally;
  }, [ratings]);

  const { strongBuy: sBuy, buy, hold, sell, strongSell: sSell } = counts;
  const total = sBuy + buy + hold + sell + sSell;
  const pct = (n: number) => (total > 0 ? `${((n / total) * 100).toFixed(1)}%` : '0%');

  const consensusPT = ratingsSummary.consensusPriceTarget ? Number(ratingsSummary.consensusPriceTarget) : null;
  const upside =
    consensusPT && currentPrice && currentPrice > 0
      ? (((consensusPT - currentPrice) / currentPrice) * 100).toFixed(1)
      : null;

  const segments = [
    { count: sBuy, label: 'S.Buy', color: COLORS.strongBuy },
    { count: buy, label: 'Buy', color: COLORS.buy },
    { count: hold, label: 'Hold', color: COLORS.hold },
    { count: sell, label: 'Sell', color: COLORS.sell },
    { count: sSell, label: 'S.Sell', color: COLORS.strongSell },
  ].filter(s => s.count > 0);

  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4 flex flex-col gap-4">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
        Analyst Consensus{total > 0 ? ` — ${total} Analysts` : ''}
      </p>

      {total > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex h-4 w-full overflow-hidden rounded-full">
            {segments.map(s => (
              <div key={s.label} style={{ width: pct(s.count), background: s.color }} />
            ))}
          </div>
          <div className="flex justify-around text-xs text-gray-500">
            {segments.map(s => (
              <div key={s.label} className="flex flex-col items-center gap-0.5">
                <span className="font-bold text-sm" style={{ color: s.color }}>
                  {s.count}
                </span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-gray-100 pt-3 text-xs">
        {ratingsSummary.consensusRating && (
          <>
            <span className="text-gray-500">Consensus Rating</span>
            <span className="font-bold text-green-600">{ratingsSummary.consensusRating}</span>
          </>
        )}
        {total > 0 && (
          <>
            <span className="text-gray-500"># Analysts</span>
            <span className="font-semibold text-gray-800">{total}</span>
          </>
        )}
        {consensusPT && (
          <>
            <span className="text-gray-500">Avg. Consensus PT</span>
            <span className="font-bold text-indigo-600">${consensusPT.toFixed(0)}</span>
          </>
        )}
        {upside !== null && (
          <>
            <span className="text-gray-500">Upside to Avg. PT</span>
            <span className={`font-bold ${Number(upside) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {Number(upside) >= 0 ? '+' : ''}
              {upside}%
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const RatingTrendChart: React.FC<{ ratings: Ratings[] }> = ({ ratings }) => {
  const snapshots = useMemo(() => buildRatingTrendSnapshots(ratings), [ratings]);
  const option = useMemo((): EChartsOption => {
    if (!snapshots.length) return {} as EChartsOption;
    const labels = snapshots.map(snapshot => snapshot.label);
    return {
      grid: { left: 36, right: 12, top: 8, bottom: 36 },
      legend: {
        data: ['S.Buy', 'Buy', 'Hold', 'Sell', 'S.Sell'],
        bottom: 0,
        textStyle: { fontSize: 9 },
        itemWidth: 10,
        itemHeight: 10,
      },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 9 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 9 }, splitLine: { lineStyle: { color: '#f0f0f0' } } },
      series: [
        {
          name: 'S.Buy',
          type: 'bar',
          stack: 'r',
          data: snapshots.map(snapshot => snapshot.strongBuy),
          itemStyle: { color: COLORS.strongBuy },
        },
        {
          name: 'Buy',
          type: 'bar',
          stack: 'r',
          data: snapshots.map(snapshot => snapshot.buy),
          itemStyle: { color: COLORS.buy },
        },
        {
          name: 'Hold',
          type: 'bar',
          stack: 'r',
          data: snapshots.map(snapshot => snapshot.hold),
          itemStyle: { color: COLORS.hold },
        },
        {
          name: 'Sell',
          type: 'bar',
          stack: 'r',
          data: snapshots.map(snapshot => snapshot.sell),
          itemStyle: { color: COLORS.sell },
        },
        {
          name: 'S.Sell',
          type: 'bar',
          stack: 'r',
          data: snapshots.map(snapshot => snapshot.strongSell),
          itemStyle: { color: COLORS.strongSell },
        },
      ],
    } as EChartsOption;
  }, [snapshots]);

  if (!snapshots.length) return null;
  return (
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">Rating Trend — Last 9 Months</p>
      <Suspense fallback={<ChartPlotSkeleton label="Loading rating trend chart" />}>
        <ReactECharts option={option} style={{ height: 220 }} />
      </Suspense>
    </div>
  );
};

interface PriceTargetsContentProps extends BaseQuotePageProps {
  ratingsSummary: AnalystRatingsSummary | null;
  analystNews: News[];
}

const PriceTargetsContent: React.FC<PriceTargetsContentProps> = ({
  analystNews,
  metaProps,
  profile,
  ratingsSummary,
  symbol,
}) => {
  const { t } = useTranslation('quote');
  const currentPrice = profile.richQuoteData?.lastTradePrice ? Number(profile.richQuoteData.lastTradePrice) : null;
  return (
    <>
      <ZacksHeadScript />
      <div className="main-div">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex flex-col gap-y-6 w-full lg:w-9/12">
            <div className="flex flex-col gap-2">
              <SectionTitle level={1} uppercase={false}>
                <QuotePageTitleLink title={metaProps.title} symbol={symbol} />
              </SectionTitle>
              <p className="section-description">{metaProps.description}</p>
            </div>

            {ratingsSummary && (ratingsSummary.ratings?.length ?? 0) > 0 && (
              <PriceTargetRangeChart ratings={ratingsSummary.ratings} summary={ratingsSummary} />
            )}

            {ratingsSummary && (ratingsSummary.ratings?.length ?? 0) > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AnalystConsensusPanel
                  currentPrice={currentPrice}
                  ratings={ratingsSummary.ratings ?? []}
                  ratingsSummary={ratingsSummary}
                />
                {(ratingsSummary.ratings?.length ?? 0) > 0 && <RatingTrendChart ratings={ratingsSummary.ratings} />}
              </div>
            )}

            {(ratingsSummary?.ratings?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-2">
                  <SectionTitle level={2} size="xl" uppercase={false}>
                    Recent Analyst Actions
                  </SectionTitle>
                  <SeeMoreLink
                    action="see_more_analyst_ratings"
                    href={seeMoreDestinations.analystRatings(symbol)}
                    label={t('Quote.SeeMore.analyst-ratings')}
                  />
                </div>
                <AnalystActionsTable ratings={ratingsSummary!.ratings} />
              </div>
            )}

            {analystNews.length > 0 && (
              <div className="flex flex-col gap-2">
                <SectionTitle level={2} size="xl" uppercase={false}>
                  Analyst Ratings News
                </SectionTitle>
                <QuoteNewsFeed
                  nodes={analystNews}
                  query={{ channels: ['Analyst Ratings'], pageSize: 6, tickers: [symbol] }}
                  seeMoreEnabled={true}
                  showLoadMoreButton={true}
                />
              </div>
            )}

            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyMobile={true} placement="incontent" />
            </Suspense>

            <QuoteFooter activeTab="price-targets" />
          </div>

          <PeopleAlsoWatch profile={profile} symbol={symbol}>
            <Suspense fallback={<div />}>
              <FreestarAd className="w-[300px] min-w-[300px]" onlyDesktop={true} placement="sidebar_2" />
            </Suspense>
          </PeopleAlsoWatch>
        </div>
      </div>
    </>
  );
};

export default PriceTargetsContent;
